import type { PortfolioReport } from '@interfaces/portfolioReport';
import { formatMoneyValue } from '@utils/moneyFormatting';
import { Alert, Box, Skeleton, Typography, useTheme } from 'hephaestus/web';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../../../hooks/useLocalization';
import type { OptimizerAuditV2Record } from '../../types/audit.types';
import type { WeightDiffDetailRow } from '../../types/snapshot.types';
import { isAuditSnapshotUnreliable } from '../../utils/audit/detectUnreliableAuditData';
import { hasPortfolioValidationWarnings } from '../../utils/audit/portfolioAuditSummary';
import { buildPortfolioAdvisorInsights } from '../../utils/insights/buildPortfolioAdvisorInsights';
import { getReportMarketValueCents } from '../../utils/presentation/reportLabels';
import { buildReportDisplayMetadata } from '../../utils/snapshot/buildReportDisplayMetadata';
import { buildTradeActivitySummary } from '../../utils/snapshot/buildTradeActivitySummary';
import { resolveValidationDisplayMessages } from '../../utils/validation/buildPortfolioValidationDiagnostics';
import { BulkRebalancePortfolioCurrencyFlags } from '../shared/portfolioCurrencyFlags';
import { BulkRebalanceRebalanceModeBadge } from '../shared/rebalanceModeBadge';
import { BulkRebalanceTradeActivityLine } from '../shared/tradeActivityLine';
import { BulkRebalanceAdvisorInsightsAccordion } from './advisorInsightsAccordion';

const ValidationMessageList = ({ messages }: { messages: string[] }) => {
  const theme = useTheme();

  if (messages.length === 0) {
    return null;
  }

  if (messages.length === 1) {
    return <Typography variant='bodySmall'>{messages[0]}</Typography>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
      {messages.map((message, index) => (
        <Box
          key={`${index}-${message}`}
          sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}
        >
          <Box
            component='span'
            sx={{
              width: 4,
              height: 4,
              borderRadius: '50%',
              mt: 1,
              flexShrink: 0,
              backgroundColor: theme.color.onSurfaceVariant,
            }}
          />
          <Typography variant='bodySmall'>{message}</Typography>
        </Box>
      ))}
    </Box>
  );
};

export type BulkRebalanceReportDetailSummaryProps = {
  reportId: string;
  report?: PortfolioReport;
  audit?: OptimizerAuditV2Record;
  diffRows: WeightDiffDetailRow[];
  clientLabel: string;
  goalLabel: string;
  reportLoading?: boolean;
  optimizerRunId?: string;
};

export const BulkRebalanceReportDetailSummary = ({
  reportId,
  report,
  audit,
  diffRows,
  clientLabel,
  goalLabel,
  reportLoading = false,
  optimizerRunId,
}: BulkRebalanceReportDetailSummaryProps) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation(['bulkRebalance', 'accountTypes']);
  const theme = useTheme();
  const marketValueCents = report ? getReportMarketValueCents(report) : 0;
  const modelLabel = report
    ? translateBackendString(report.modelPortfolio?.translatedName)
    : '';
  const reportMetadata = useMemo(
    () =>
      report
        ? buildReportDisplayMetadata(translateBackendString, report, t)
        : undefined,
    [report, t],
  );
  const tradeActivity = useMemo(
    () => buildTradeActivitySummary(diffRows, audit),
    [audit, diffRows],
  );
  const hasValidationFailure = audit?.passed === false;
  const auditUnreliable = useMemo(
    () => isAuditSnapshotUnreliable(audit),
    [audit],
  );
  const hasValidationWarnings = useMemo(
    () => hasPortfolioValidationWarnings(audit),
    [audit],
  );
  const validationMessages = useMemo(() => {
    if (!hasValidationFailure && !hasValidationWarnings) {
      return [];
    }

    return resolveValidationDisplayMessages(
      translateBackendString,
      report,
      audit?.validationErrors ?? [],
      t,
    );
  }, [
    audit?.validationErrors,
    hasValidationFailure,
    hasValidationWarnings,
    report,
    t,
  ]);
  const advisorInsights = useMemo(
    () => buildPortfolioAdvisorInsights(audit, report),
    [audit, report],
  );
  const showAdvisorInsights = Boolean(advisorInsights) && !hasValidationFailure;

  const contextAlert = useMemo(() => {
    if (hasValidationFailure) {
      return (
        <Alert
          severity='warning'
          sx={{ mt: 1.5, py: 1, alignItems: 'flex-start' }}
          data-testid='bulk-rebalance-report-validation-notice'
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant='bodySmall' sx={{ fontWeight: 600 }}>
              {t('drawer.validationFailedTitle')}
            </Typography>
            <ValidationMessageList messages={validationMessages} />
          </Box>
        </Alert>
      );
    }

    if (hasValidationWarnings) {
      return (
        <Alert
          severity='info'
          sx={{ mt: 1.5, py: 1, alignItems: 'flex-start' }}
          data-testid='bulk-rebalance-report-validation-warning-notice'
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant='bodySmall' sx={{ fontWeight: 600 }}>
              {t('drawer.validationWarningTitle')}
            </Typography>
            <ValidationMessageList messages={validationMessages} />
          </Box>
        </Alert>
      );
    }

    return null;
  }, [hasValidationFailure, hasValidationWarnings, t, validationMessages]);

  const contextParts = report
    ? [
        t(`scopeType.${report.modelPortfolioType}`),
        modelLabel,
        formatMoneyValue(marketValueCents),
      ].filter(Boolean)
    : [clientLabel, goalLabel].filter(Boolean);

  return (
    <Box
      data-testid='bulk-rebalance-report-detail-summary'
      sx={{
        px: 3,
        pt: 0.75,
        pb: 2,
        borderBottom: '1px solid',
        borderColor: theme.color.outlineVariant,
      }}
    >
      <Typography
        variant='labelSmall'
        sx={{ fontWeight: 600 }}
        color='textSecondary'
        data-testid='bulk-rebalance-snapshot-notice'
      >
        {t('drawer.snapshotNotice')}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          mt: 1.25,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.75,
            alignItems: 'center',
          }}
        >
          <BulkRebalanceRebalanceModeBadge audit={audit} />
          <BulkRebalancePortfolioCurrencyFlags report={report} />
          <Typography variant='bodySmall' color='textSecondary'>
            {reportLoading ? (
              <Skeleton variant='text' width='60%' />
            ) : (
              contextParts.join(' · ')
            )}
          </Typography>
        </Box>

        {tradeActivity && (
          <BulkRebalanceTradeActivityLine
            summary={tradeActivity}
            layout='inline'
          />
        )}
      </Box>

      {contextAlert}

      {auditUnreliable && !hasValidationFailure && (
        <Alert
          severity='warning'
          sx={{ mt: 1.5 }}
          data-testid='bulk-rebalance-report-unreliable-notice'
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant='bodyMedium' sx={{ fontWeight: 600 }}>
              {t('drawer.unreliableSnapshotTitle')}
            </Typography>
            <Typography variant='bodySmall'>
              {t('drawer.unreliableSnapshotBody')}
            </Typography>
          </Box>
        </Alert>
      )}

      {!report && audit && !reportLoading && (
        <Alert
          severity='info'
          sx={{ mt: 1.5 }}
          data-testid='bulk-rebalance-report-unresolved-notice'
        >
          <Typography variant='bodySmall'>
            {t('drawer.unresolvedPortfolioNotice', { reportId })}
          </Typography>
        </Alert>
      )}

      {showAdvisorInsights && reportMetadata && (
        <BulkRebalanceAdvisorInsightsAccordion
          audit={audit}
          embedded
          subAccountsById={reportMetadata.subAccountsById}
          report={report}
          diffRows={diffRows}
          optimizerRunId={optimizerRunId}
          clientLabel={clientLabel}
          goalLabel={goalLabel}
          unreliable={auditUnreliable}
        />
      )}
    </Box>
  );
};
